import { useCallback, useState } from 'react';
import { Node, Edge } from 'reactflow';
import { nanoid } from 'nanoid';
import { useWorkflowStore } from '@/stores/workflow-store';
import { toast } from 'sonner';
import { useManualPaste } from '@/contexts/ManualPasteContext';
import {
  detectWorkflowFormat,
  isValidN8nWorkflow,
  type N8nWorkflow,
} from '@/lib/n8n-import';
import { generateUniqueNodeLabel, getNodeLabels } from '@/utils/node-label-utils';

interface ClipboardData {
  nodes: Node[];
  edges: Edge[];
  type: 'copy' | 'cut';
  source: 'evo-e2e-workflow' | 'external';
  timestamp: number;
}

// n8n import dialog state (exported for use in WorkflowEditor)
export interface N8nImportState {
  isOpen: boolean;
  workflow: N8nWorkflow | null;
}

let clipboard: ClipboardData | null = null;

// localStorage key for clipboard fallback
const CLIPBOARD_STORAGE_KEY = 'evo-e2e-clipboard';

export function useCanvasOperations() {
  const { nodes, edges, addNode, updateNode, deleteNode, addEdge, deleteEdge, setNodes } =
    useWorkflowStore();
  const { showManualPasteDialog } = useManualPaste();

  // n8n import dialog state
  const [n8nImportState, setN8nImportState] = useState<N8nImportState>({
    isOpen: false,
    workflow: null,
  });

  // Handler to open n8n import dialog
  const openN8nImportDialog = useCallback((workflow: N8nWorkflow) => {
    setN8nImportState({ isOpen: true, workflow });
  }, []);

  // Handler to close n8n import dialog
  const closeN8nImportDialog = useCallback(() => {
    setN8nImportState({ isOpen: false, workflow: null });
  }, []);

  // Function to check if clipboard has data
  const hasClipboardData = useCallback(async (): Promise<boolean> => {
    // Check memory clipboard first
    if (clipboard && clipboard.nodes && clipboard.nodes.length > 0) {
      return true;
    }

    // Try system clipboard
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        try {
          const parsed = JSON.parse(text);
          // Check for internal format OR generic workflow format (nodes + edges arrays)
          if ((parsed.nodes && parsed.edges && parsed.type && parsed.source === 'evo-e2e-workflow') ||
              (parsed.nodes && Array.isArray(parsed.nodes) && parsed.edges && Array.isArray(parsed.edges))) {
            return true;
          }
        } catch (parseError) {
          // Not valid JSON, ignore
        }
      }
    } catch (error) {
      // If clipboard API is blocked by permissions policy, always return true
      // This will enable the paste option and show a manual paste dialog
      if (error instanceof Error && error.name === 'NotAllowedError') {
        return true;
      }
      console.warn('Could not read system clipboard:', error);
    }

    // Try localStorage
    try {
      const stored = localStorage.getItem(CLIPBOARD_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.nodes && parsed.edges && parsed.type) {
          return true;
        }
      }
    } catch (error) {
      // Ignore localStorage errors
    }

    return false;
  }, []);

  const copyNodes = useCallback(
    async (nodesToCopy: Node[]) => {
      if (nodesToCopy.length === 0) return;

      const nodeIds = nodesToCopy.map((n) => n.id);
      const relatedEdges = edges.filter(
        (e) => nodeIds.includes(e.source) && nodeIds.includes(e.target)
      );

      clipboard = {
        nodes: nodesToCopy,
        edges: relatedEdges,
        type: 'copy',
        source: 'evo-e2e-workflow',
        timestamp: Date.now(),
      };

      // ✅ FIX: Copy to system clipboard with localStorage fallback
      try {
        const clipboardText = JSON.stringify(clipboard);
        await navigator.clipboard.writeText(clipboardText);
        toast.success(`Copied ${nodesToCopy.length} node(s) to clipboard`);
      } catch (error) {
        try {
          localStorage.setItem(CLIPBOARD_STORAGE_KEY, JSON.stringify(clipboard));
          toast.success(`Copied ${nodesToCopy.length} node(s)`);
        } catch (storageError) {
          // console.error('Failed to write to localStorage:', storageError);
          toast.success(`Copied ${nodesToCopy.length} node(s) (memory only)`);
        }
      }
    },
    [edges]
  );

  const cutNodes = useCallback(
    async (nodesToCut: Node[]) => {
      if (nodesToCut.length === 0) return;

      await copyNodes(nodesToCut);
      clipboard!.type = 'cut';

      // Delete nodes after copying
      nodesToCut.forEach((node) => deleteNode(node.id));
    },
    [copyNodes, deleteNode]
  );

  const pasteNodes = useCallback(async () => {
    // ✅ FIX: Try to read from system clipboard first, then localStorage, then memory
    let clipboardData = clipboard;
    let clipboardBlocked = false;

    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        const parsed = JSON.parse(text);

        // 🆕 Check for n8n format FIRST (before other formats)
        const format = detectWorkflowFormat(parsed);
        if (format === 'n8n' && isValidN8nWorkflow(parsed)) {
          openN8nImportDialog(parsed as N8nWorkflow);
          return; // Exit early - dialog will handle the import
        }

        // Try internal format first (with source marker)
        if (parsed.nodes && parsed.edges && parsed.type && parsed.source === 'evo-e2e-workflow') {
          clipboardData = parsed;
        }
        // Try generic workflow format (just nodes and edges from external source)
        else if (parsed.nodes && Array.isArray(parsed.nodes) && parsed.edges && Array.isArray(parsed.edges)) {
          clipboardData = {
            nodes: parsed.nodes,
            edges: parsed.edges,
            type: 'copy',
            source: 'external',
            timestamp: Date.now(),
          };
          toast.info('Pasting workflow from external source');
        }
      }
    } catch (error) {
      console.warn('🔴 Error reading clipboard:', error);

      // Check if clipboard API is blocked
      if (error instanceof Error && error.name === 'NotAllowedError') {
        clipboardBlocked = true;
      }

      // Try localStorage fallback
      if (!clipboardBlocked) {
        try {
          const stored = localStorage.getItem(CLIPBOARD_STORAGE_KEY);
          if (stored) {
            const parsed = JSON.parse(stored);
            if (parsed.nodes && parsed.edges && parsed.type) {
              clipboardData = parsed;
            }
          }
        } catch (storageError) {
          console.warn('Error reading localStorage:', storageError);
        }
      }
    }

    // If clipboard is blocked and we have no data, show manual paste dialog
    if (clipboardBlocked && !clipboardData) {
      showManualPasteDialog((pastedText: string) => {
        if (pastedText) {
          try {
            const parsed = JSON.parse(pastedText);

            // 🆕 Check for n8n format in manual paste
            const format = detectWorkflowFormat(parsed);
            if (format === 'n8n' && isValidN8nWorkflow(parsed)) {
              openN8nImportDialog(parsed as N8nWorkflow);
              return; // Exit early - dialog will handle the import
            }

            if (parsed.nodes && Array.isArray(parsed.nodes) && parsed.edges && Array.isArray(parsed.edges)) {
              const manualClipboardData = {
                nodes: parsed.nodes,
                edges: parsed.edges,
                type: 'copy' as const,
                source: 'external' as const,
                timestamp: Date.now(),
              };
              
              // Process the paste inline
              const idMap = new Map<string, string>();
              const existingLabels = getNodeLabels(nodes);
              const usedLabels = [...existingLabels];

              const newNodes = manualClipboardData.nodes.map((node) => {
                const newId = nanoid();
                idMap.set(node.id, newId);

                // Generate unique label
                const baseLabel = node.data?.label || node.type || 'Node';
                const uniqueLabel = generateUniqueNodeLabel(baseLabel, usedLabels);
                usedLabels.push(uniqueLabel);

                return {
                  ...node,
                  id: newId,
                  position: {
                    x: node.position.x + 50,
                    y: node.position.y + 50,
                  },
                  selected: true,
                  data: {
                    ...node.data,
                    label: uniqueLabel,
                  },
                };
              });

              const newEdges = manualClipboardData.edges.map((edge) => ({
                ...edge,
                id: nanoid(),
                source: idMap.get(edge.source) || edge.source,
                target: idMap.get(edge.target) || edge.target,
                selected: true,
              }));

              setNodes((prevNodes) =>
                prevNodes.map((n) => ({ ...n, selected: false })).concat(newNodes)
              );

              newEdges.forEach((edge) => {
                addEdge(edge);
              });

              toast.success('Workflow pasted successfully');
            } else {
              toast.error('Invalid workflow format. Must contain "nodes" and "edges" arrays.');
            }
          } catch (parseError) {
            toast.error('Invalid JSON format');
          }
        }
      });
      return; // Exit early, the dialog callback will handle the paste
    }

    if (!clipboardData) {
      console.error('❌ No clipboard data found');
      toast.error('Nothing to paste');
      return;
    }

    const idMap = new Map<string, string>();

    // Get existing labels for unique label generation
    // We need to track labels as we add them to handle multiple nodes being pasted
    const existingLabels = getNodeLabels(nodes);
    const usedLabels = [...existingLabels];

    // Create new nodes with new IDs, offset positions, and unique labels
    const newNodes = clipboardData.nodes.map((node) => {
      const newId = nanoid();
      idMap.set(node.id, newId);

      // Generate unique label and track it for subsequent nodes
      const baseLabel = node.data?.label || node.type || 'Node';
      const uniqueLabel = generateUniqueNodeLabel(baseLabel, usedLabels);
      usedLabels.push(uniqueLabel);

      return {
        ...node,
        id: newId,
        position: {
          x: node.position.x + 50,
          y: node.position.y + 50,
        },
        selected: false,
        data: {
          ...node.data,
          label: uniqueLabel,
        },
      };
    });

    // Create new edges with updated node IDs
    const newEdges = clipboardData.edges.map((edge) => ({
      ...edge,
      id: nanoid(),
      source: idMap.get(edge.source) || edge.source,
      target: idMap.get(edge.target) || edge.target,
    }));

    // Add nodes and edges
    newNodes.forEach((node) => addNode(node));
    newEdges.forEach((edge) => addEdge(edge));

    // If it was a cut operation, clear clipboard
    if (clipboardData.type === 'cut') {
      clipboard = null;
    }

    toast.success(`Pasted ${newNodes.length} node(s)`);
  }, [addNode, addEdge, nodes]);

  const duplicateNodes = useCallback(
    (nodesToDuplicate: Node[]) => {
      if (nodesToDuplicate.length === 0) return;

      const idMap = new Map<string, string>();

      // Get existing labels for unique label generation
      const existingLabels = getNodeLabels(nodes);
      const usedLabels = [...existingLabels];

      // Create duplicates with new IDs, offset positions, and unique labels
      const duplicates = nodesToDuplicate.map((node) => {
        const newId = nanoid();
        idMap.set(node.id, newId);

        // Generate unique label
        const baseLabel = node.data?.label || node.type || 'Node';
        const uniqueLabel = generateUniqueNodeLabel(baseLabel, usedLabels);
        usedLabels.push(uniqueLabel);

        return {
          ...node,
          id: newId,
          position: {
            x: node.position.x + 50,
            y: node.position.y + 50,
          },
          selected: false,
          data: {
            ...node.data,
            label: uniqueLabel,
          },
        };
      });

      // Find edges between duplicated nodes
      const nodeIds = nodesToDuplicate.map((n) => n.id);
      const relatedEdges = edges.filter(
        (e) => nodeIds.includes(e.source) && nodeIds.includes(e.target)
      );

      // Create duplicate edges
      const duplicateEdges = relatedEdges.map((edge) => ({
        ...edge,
        id: nanoid(),
        source: idMap.get(edge.source) || edge.source,
        target: idMap.get(edge.target) || edge.target,
      }));

      // Add duplicates
      duplicates.forEach((node) => addNode(node));
      duplicateEdges.forEach((edge) => addEdge(edge));

      toast.success(`Duplicated ${duplicates.length} node(s)`);
    },
    [edges, nodes, addNode, addEdge]
  );

  const deleteSelectedNodes = useCallback(() => {
    const selectedNodes = nodes.filter((n) => n.selected);
    selectedNodes.forEach((node) => deleteNode(node.id));
  }, [nodes, deleteNode]);

  const selectAllNodes = useCallback(() => {
    const updatedNodes = nodes.map((node) => ({
      ...node,
      selected: true,
    }));
    setNodes(updatedNodes);
  }, [nodes, setNodes]);

  const deselectAllNodes = useCallback(() => {
    const updatedNodes = nodes.map((node) => ({
      ...node,
      selected: false,
    }));
    setNodes(updatedNodes);
  }, [nodes, setNodes]);

  return {
    copyNodes,
    cutNodes,
    pasteNodes,
    duplicateNodes,
    deleteSelectedNodes,
    selectAllNodes,
    deselectAllNodes,
    hasClipboardData,
    // n8n import
    n8nImportState,
    closeN8nImportDialog,
  };
}
